import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@nextui-org/react'
import { Typography } from '~/components/common/Typography'

export interface ExampleData {
  id: string
  input: string
  output: string
}

export interface AddExamplesModalProps {
  isOpen: boolean
  onClose: () => void
  flowId: string
  flowName: string
  initialExamples?: ExampleData[]
  onSave: (flowId: string, examples: ExampleData[]) => void
}

// SVG Icons
const CloseIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M18 6L6 18M6 6L18 18'
      stroke='black'
      strokeOpacity='0.45'
      strokeWidth='2'
      strokeLinecap='round'
    />
  </svg>
)

const PlusIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M8 3.33333V12.6667M3.33333 8H12.6667'
      stroke='black'
      strokeOpacity='0.88'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

const DeleteIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 4L4 12M4 4L12 12'
      stroke='black'
      strokeOpacity='0.45'
      strokeWidth='1.5'
      strokeLinecap='round'
    />
  </svg>
)

export function AddExamplesModal({
  isOpen,
  onClose,
  flowId,
  flowName,
  initialExamples,
  onSave
}: AddExamplesModalProps) {
  const [examples, setExamples] = useState<ExampleData[]>([
    { id: crypto.randomUUID(), input: '', output: '' }
  ])
  const firstInputRef = useRef<HTMLTextAreaElement>(null)
  const previousIsOpenRef = useRef(false)

  // Initialize examples from props only when modal transitions from closed to open
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      // Modal just opened - initialize with props
      if (initialExamples && initialExamples.length > 0) {
        setExamples(initialExamples)
      } else {
        setExamples([{ id: crypto.randomUUID(), input: '', output: '' }])
      }
    }
    previousIsOpenRef.current = isOpen
  }, [isOpen, initialExamples])

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  const handleAddExample = useCallback(() => {
    setExamples(prev => [...prev, { id: crypto.randomUUID(), input: '', output: '' }])
  }, [])

  const handleDeleteExample = useCallback((id: string) => {
    setExamples(prev => {
      // Keep at least one example
      if (prev.length <= 1) return prev
      return prev.filter(ex => ex.id !== id)
    })
  }, [])

  const handleInputChange = useCallback((id: string, field: 'input' | 'output', value: string) => {
    setExamples(prev => prev.map(ex => (ex.id === id ? { ...ex, [field]: value } : ex)))
  }, [])

  const handleSave = useCallback(() => {
    // Filter out empty examples
    const validExamples = examples.filter(ex => ex.input.trim() || ex.output.trim())

    if (validExamples.length === 0) {
      // Show error or just close - for now just return
      return
    }

    onSave(flowId, validExamples)
  }, [flowId, examples, onSave])

  if (!isOpen) return null

  return createPortal(
    <div
      className='fixed inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.45)]'
      style={{ zIndex: 9999 }}
      onClick={handleOverlayClick}
      role='dialog'
      aria-modal='true'
      aria-labelledby='modal-title'
    >
      <div
        className='flex w-[1034px] h-[90vh] max-h-[90vh] flex-col overflow-hidden rounded-[28px] bg-white shadow-[0px_4px_16px_rgba(0,0,0,0.12)]'
        onClick={e => e.stopPropagation()}
      >
        {/* Header - Fixed at top */}
        <div className='flex shrink-0 items-center justify-between border-b border-[rgba(0,0,0,0.08)] px-[32px] py-[24px]'>
          <div className='flex flex-col gap-[4px]'>
            <Typography
              id='modal-title'
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-black'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
            >
              Add examples
            </Typography>
            <Typography
              fontFamily='geistMono'
              fontSize='bodySm'
              fontWeight='semibold'
              className='text-[rgba(0,0,0,0.65)]'
              style={{ fontSize: '12px', fontWeight: 600 }}
            >
              {flowName}
            </Typography>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='flex h-[24px] w-[24px] cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-opacity hover:opacity-70'
            aria-label='Close modal'
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div
          className='flex flex-1 flex-col gap-[16px] overflow-y-auto px-[32px] py-[32px]'
          style={{ minHeight: 0 }}
        >
          {examples.map((example, index) => (
            <div key={example.id} className='relative flex gap-[24px]'>
              {/* Expected Input */}
              <div className='flex flex-1 flex-col gap-[8px]'>
                <div className='flex h-[24px] items-center justify-between'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='semibold'
                    className='text-[rgba(0,0,0,0.88)]'
                    style={{ fontSize: '14px', fontWeight: 600, lineHeight: '18px' }}
                  >
                    Expected Input
                  </Typography>
                </div>
                <textarea
                  ref={index === 0 ? firstInputRef : undefined}
                  data-example-input={index}
                  value={example.input}
                  onChange={e => handleInputChange(example.id, 'input', e.target.value)}
                  placeholder='Enter expected input JSON...'
                  className={cn(
                    'h-[438px] w-[442px] resize-none rounded-[16px]',
                    'border border-[rgba(0,0,0,0.12)] bg-white p-[16px]',
                    'font-geist text-[14px] font-medium leading-[22px]',
                    'text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)]',
                    'focus:border-[rgb(132,111,225)] focus:outline-none'
                  )}
                />
              </div>

              {/* Expected Output */}
              <div className='flex flex-1 flex-col gap-[8px]'>
                <div className='flex h-[24px] items-center justify-between'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='semibold'
                    className='text-[rgba(0,0,0,0.88)]'
                    style={{ fontSize: '14px', fontWeight: 600, lineHeight: '18px' }}
                  >
                    Expected Output
                  </Typography>
                  {examples.length > 1 && (
                    <button
                      type='button'
                      onClick={() => handleDeleteExample(example.id)}
                      className='flex h-[24px] w-[24px] cursor-pointer items-center justify-center border-0 bg-transparent p-0 transition-opacity hover:opacity-70'
                      aria-label='Delete example'
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </div>
                <textarea
                  data-example-output={index}
                  value={example.output}
                  onChange={e => handleInputChange(example.id, 'output', e.target.value)}
                  placeholder='Enter expected output JSON...'
                  className={cn(
                    'h-[438px] w-[442px] resize-none rounded-[16px]',
                    'border border-[rgba(0,0,0,0.12)] bg-white p-[16px]',
                    'font-geist text-[14px] font-medium leading-[22px]',
                    'text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)]',
                    'focus:border-[rgb(132,111,225)] focus:outline-none'
                  )}
                />
              </div>
            </div>
          ))}

          {/* More Examples Button */}
          <button
            type='button'
            onClick={handleAddExample}
            className={cn(
              'flex cursor-pointer items-center justify-center gap-[8px] self-start',
              'rounded-[12px] border border-[rgba(0,0,0,0.12)] bg-transparent',
              'px-[16px] py-[8px] transition-colors hover:bg-[rgba(0,0,0,0.02)]'
            )}
          >
            <PlusIcon />
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-[rgba(0,0,0,0.88)]'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              More Examples
            </Typography>
          </button>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className='flex shrink-0 justify-end border-t border-[rgba(0,0,0,0.08)] px-[32px] py-[16px]'>
          <button
            type='button'
            onClick={handleSave}
            className={cn(
              'cursor-pointer rounded-[12px] border-0 bg-[#262626]',
              'px-[16px] py-[8px] transition-colors hover:bg-[#383838]'
            )}
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              className='text-white'
              style={{ fontSize: '14px', fontWeight: 500, lineHeight: '22px' }}
            >
              Save
            </Typography>
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default AddExamplesModal
